// A department's distinguished alumni, as the department page lists them and
// as each one's profile page shows them.
//
// Where webadmin has published `notableAlumni` for the department (on its
// record in the `departments` collection), those are the list, exactly as
// published: the same people in the same order, with their published name,
// role, quote and photo. The department's own `alumni` list in departments.ts
// is shown only where it has published none, and has no profile pages. The two
// are never merged — that could only pair people up by guessing at name
// matches.
//
// Each published alumnus has a profile at /department/<dept>/alumni/<slug>
// showing only their published fields. The record carries no id, so the slug
// is made from the published name: lower case, every run of other characters
// one hyphen, and -2, -3… for a repeat within the department. It is only the
// address; every page shows the name as published.
//
// Photos: webadmin publishes full URLs, and the deploy's
// process-content-images.mjs rewrites each to a bare key on our media bucket
// before the build. asMedia keeps only bare keys, so content fetched locally
// with `pnpm fetch:content` alone (no processing) shows portrait placeholders.
import type { Media } from "@zivin-labs/site-content";
import type { DeptAlumnus } from "./departments";
import { resolveDepartment } from "./departments";
import { useCollection } from "./siteContent";

interface PublishedMediaRef {
  url: string;
  alt?: string;
  width?: number;
  height?: number;
}

interface PublishedAlumnusRef {
  name: string;
  currentRole?: string | null;
  organization?: string | null;
  quote?: string | null;
  graduationYear?: number | null;
  photo?: PublishedMediaRef | null;
}

interface PublishedDeptAlumniRecord {
  code: string;
  name?: string;
  notableAlumni?: PublishedAlumnusRef[] | null;
}

/** One alumnus exactly as webadmin publishes them, and the address of their profile. */
export interface PublishedAlumnus {
  slug: string;
  name: string;
  currentRole: string;
  organization: string;
  quote: string;
  graduationYear?: number;
  photo?: Media;
}

/** A department page's alumni card; `profileSlug` is set when it comes from published data. */
export interface DepartmentAlumnus extends DeptAlumnus {
  profileSlug?: string;
}

/** Same bare-key discipline as publishedDepartmentExtras.ts's asMedia — never route an unprocessed webadmin URL through the transform CDN. */
function asMedia(ref: PublishedMediaRef | null | undefined, fallbackAlt: string): Media | undefined {
  if (!ref?.url || ref.url.includes("://") || !ref.width || !ref.height) return undefined;
  return { url: ref.url, alt: ref.alt || fallbackAlt, width: ref.width, height: ref.height };
}

/** The department's published alumni and its published name; `loading` until the content has arrived. */
function usePublishedDepartmentAlumni(code: string | undefined) {
  const { items, loading } = useCollection("departments");
  const records = items as unknown as PublishedDeptAlumniRecord[];
  const rec = code
    ? records.find((r) => (resolveDepartment(r.code)?.code ?? "").toUpperCase() === code.toUpperCase())
    : undefined;

  const taken = new Set<string>();
  const alumni = (rec?.notableAlumni ?? [])
    .filter((p) => p.name?.trim())
    .map((p): PublishedAlumnus => {
      const name = p.name.trim();
      const base = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "alumnus";
      let slug = base;
      for (let n = 2; taken.has(slug); n++) slug = `${base}-${n}`;
      taken.add(slug);
      return {
        slug,
        name,
        currentRole: p.currentRole?.trim() ?? "",
        organization: p.organization?.trim() ?? "",
        quote: p.quote?.trim() ?? "",
        graduationYear: p.graduationYear ?? undefined,
        photo: asMedia(p.photo, name),
      };
    });
  return { loading, departmentName: rec?.name ?? null, alumni };
}

export function useDepartmentAlumni(code: string | undefined, localAlumni: DeptAlumnus[]): DepartmentAlumnus[] {
  const { alumni } = usePublishedDepartmentAlumni(code);
  if (alumni.length === 0) return localAlumni;
  return alumni.map((a) => ({
    name: a.name,
    // "Director at Acme" — the card has one line for the published role and organization.
    role: a.currentRole && a.organization ? `${a.currentRole} at ${a.organization}` : a.currentRole,
    bio: a.quote,
    photo: a.photo,
    profileSlug: a.slug,
  }));
}

/** One published alumnus of the department by profile address, and the department's others. */
export function usePublishedAlumnus(code: string | undefined, slug: string) {
  const { loading, departmentName, alumni } = usePublishedDepartmentAlumni(code);
  return {
    loading,
    departmentName,
    alumnus: alumni.find((a) => a.slug === slug),
    others: alumni.filter((a) => a.slug !== slug),
  };
}
